import React from 'react'

import memoizeOne from 'memoize-one'
import {
  always,
  clamp,
  either,
  identity,
  path,
  pick,
  pipe,
  toLower,
  uniqBy,
} from 'ramda'

import { Fade, Tooltip } from '@mui/material'

import { ANNOTATION_WIDTH } from '~/src/Lib/Constants'
import createLogger from '~/src/Lib/Logging'
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  getCollidesWith,
  getDateTime,
  memoize,
} from '~/src/Lib/Utils'
import { selectFrom, selectTo } from '~/src/Room/bundle'

import { annotationTypeMap } from '../constants'

export { default as schemaFormatters } from './schemaFormatters'
export * from './useAllowedAnnotationTypes'

const ENTER_DELAY = 250
const LEAVE_DELAY = 500

const logger = createLogger('Annotations#utils')

export const getTypes = key => key && key.split('_').slice(0, 2)
export const getType = key => {
  if (!key) return key
  const [type] = getTypes(key)
  return type
}
export const sortAnnotationsByDate = (a, b) => {
  const aDate = a.annotation.timestamp
  const bDate = b.annotation.timestamp
  if (aDate > bDate) return -1
  if (aDate < bDate) return 1
  return 0
}

const collidesWith = getCollidesWith('left', ANNOTATION_WIDTH)

export const selectCollisions = memoizeOne(annotationAnchors => {
  const anchors = Object.entries(annotationAnchors).sort(
    ([, a], [, b]) => a.left - b.left
  )

  return anchors.reduce((acc, [key, outer]) => {
    // We'll get a mean of all colliding lefts in this group for our grouped left
    const lefts = [outer.left]
    //
    const prev = Object.values(acc).find(set => {
      const alreadyGrouped = set.keys.includes(key)
      const collidesWithGroup = collidesWith(outer, set)
      return alreadyGrouped || collidesWithGroup
    })
    if (prev) {
      const { keys } = prev
      if (!keys.includes(key)) {
        keys.push(key)
        prev.left = keys.reduce((sum, k) => sum + annotationAnchors[k].left, 0) / keys.length
      }
      return acc
    }
    let hasCollisions = false
    const colliding = anchors.filter(([innerKey, inner]) => {
      if (inner === outer) return false
      if (!collidesWith(prev || outer, inner)) return false
      const innerPrev = Object.values(acc).find(set => set.keys.includes(innerKey))
      if (innerPrev && !collidesWith(outer, innerPrev)) return false
      lefts.push(inner.left)
      hasCollisions = true
      return true
    })
    const left = lefts.reduce((sum, l) => sum + l) / lefts.length
    // if we have no collisions or the collisions have the same left
    // (and thus have the same set of coordinates), don't add anything
    const keys = colliding.map(([k]) => k)
    keys.unshift(key)
    keys.sort()
    if (
      !hasCollisions
      || (prev && prev.left === left && prev.keys.length === keys.length)
    ) {
      return acc
    }
    // return EMPTY_ARRAY
    return [...acc, { left, keys }]
  }, EMPTY_ARRAY)
})

export const groupAnnotationsByType = memoizeOne(annotations => (annotations
  ? annotations.reduce((acc, entry) => {
    const { schema: type } = entry
    if (!type) {
      return acc
    }
    return { ...acc, [type]: [...(acc[type] || EMPTY_ARRAY), entry] }
  }, EMPTY_OBJECT)
  : null))

export const Tooltipper = React.memo(({ classes, ...props }) => React.createElement(Tooltip, {
  TransitionComponent: Fade,
  classes: { tooltip: classes.tooltip },
  enterDelay: ENTER_DELAY,
  leaveDelay: LEAVE_DELAY,
  placement: 'bottom',
  ...props
}))

export const getAnnotationAnchors = memoizeOne(({ journal, xScale, chartWidth }) => {
  const halfAnnotation = ANNOTATION_WIDTH * 0.50

  const maxLeft = chartWidth - halfAnnotation
  const minLeft = halfAnnotation
  const clampLeft = clamp(minLeft, Math.max(minLeft, maxLeft))

  return journal?.items?.length
    ? journal.items.reduce(
      (acc, { uuid, timestamp }) => ({
        ...acc,
        [`${uuid}--${timestamp}`]: { left: clampLeft(xScale(new Date(timestamp))) }
      }),
      EMPTY_OBJECT
    )
    : EMPTY_OBJECT
})

export const getBehavior = pipe(
  path(['category', 'behavior']),
  // if behavior is falsy, coerce to empty string
  either(identity, always('')),
  toLower
)

export const getKey = ({ uuid, timestamp }) => `${uuid}--${timestamp}`

export const uniqueByKey = uniqBy(getKey)

export const getFilteredAnnotations = memoize(({
  annotations = EMPTY_ARRAY,
  anchors: annotationAnchors,
  context,
  entities,
}) => {
  const {
    room,
    growlog,
    individualGraphs,
    mainGraphZone,
    selectedHarvest,
    selectedZones,
    activeAnnotationTypes: activeTypes,
    activeAnnotationUser: activeUser,
  } = context
  const range = selectFrom(context).until((selectTo(context) ?? getDateTime('now').plus({ hours: 1 })))

  if (!annotations?.length) {
    return [EMPTY_ARRAY, EMPTY_OBJECT]
  }

  const filteredAnnotations = annotations.filter(a => {
    const hasSchema = Boolean(a.schema)
    const timestamp = getDateTime(a.localTimestamp ?? a.timestamp)
    const inTimeframe = range.contains(timestamp)
    const typeIsActive = activeTypes.some(type => {
      const { [type]: config } = annotationTypeMap
      return a.schema === config?.schema || a.schema === config?.alternateSchema
    })
    let allowed = hasSchema && inTimeframe && typeIsActive
    if (activeUser) {
      allowed = a.relatedUser === activeUser.id
    }

    if (!allowed) return allowed

    const annotation = a.annotation ?? a.data ?? entities?.[a.schema]?.[a.id ?? a.data?.id]

    if (!annotation) {
      console.warn(
        'getFilteredAnnotations | annotation missing:',
        a,
        entities[a.schema]
      )
      return false
    }

    if (room && room.id && annotation.room && annotation.room !== room.id) {
      return false
    }

    const growlogOtherHarvest = growlog
      && (
        (a.schema === 'harvests' && a.id !== selectedHarvest?.id)
        || (a.schema === 'harvestPhases' && annotation.harvestId !== selectedHarvest?.id)
      )
    if (growlogOtherHarvest) {
      return false
    }

    const notInSelectedZones = !growlog && (
      annotation.zone
      && (
        !selectedZones.includes(annotation.zone)
        || (individualGraphs && typeof mainGraphZone === 'number' && mainGraphZone !== annotation.zone)
      )
    )
    if (notInSelectedZones) {
      allowed = false
    } else {
      const phase = annotation.phaseId ?? annotation.phase?.id ?? annotation.phase
      allowed = (
        !selectedHarvest
          || !selectedHarvest?.phases?.length
          || !phase
          || selectedHarvest?.phases?.find(p => phase === (p?.id ?? p))
      )
    }

    return allowed
  })

  return [
    uniqueByKey(filteredAnnotations),
    annotationAnchors ? pick(
      filteredAnnotations.map(getKey),
      annotationAnchors
    ) : EMPTY_OBJECT,
  ]
})
